import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { validatePassword } from '../../../../utils/passwordValidation';
import PasswordRequirements from '../../../../components/PasswordRequirements';
import { Lock, Save, X, Check } from 'lucide-react';

const Profile = () => {
  const { currentUser, changePassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form data for profile update
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    language: 'en',
    timezone: 'CST',
    softeonUsername: '',
    lvUsername: '',
    locusUsername: ''
  });
  
  // Form validation errors
  const [formErrors, setFormErrors] = useState({});
  
  // Password change modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  // API URL
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  
  // Initialize form data with current user's info
  useEffect(() => {
    if (currentUser) {
      setFormData({
        fullName: currentUser.full_name || '',
        email: currentUser.email || '',
        phoneNumber: currentUser.phone_number || '',
        language: currentUser.language || 'en',
        timezone: currentUser.timezone || 'CST',
        softeonUsername: currentUser.softeon_username || '',
        lvUsername: currentUser.lv_username || '',
        locusUsername: currentUser.locus_username || ''
      });
    }
  }, [currentUser]);
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle password field changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    
    setPasswordData({
      ...passwordData,
      [name]: value
    });
    
    // Validate new password as user types
    if (name === 'newPassword') {
      const validation = validatePassword(value, currentUser.role);
      setPasswordValidation(validation);
    }
  };
  
  // Validate main form
  const validateForm = () => {
    const errors = {};
    
    // Email validation - ensure it's a valid email format
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Phone number validation - simple length check
    if (formData.phoneNumber && formData.phoneNumber.length > 20) {
      errors.phoneNumber = 'Phone number must be less than 20 characters';
    }
    
    // External systems username checks
    if (formData.softeonUsername && formData.softeonUsername.length > 100) {
      errors.softeonUsername = 'Softeon username must be less than 100 characters';
    }
    
    if (formData.lvUsername && formData.lvUsername.length > 100) {
      errors.lvUsername = 'LV username must be less than 100 characters';
    }
    
    if (formData.locusUsername && formData.locusUsername.length > 100) {
      errors.locusUsername = 'Locus username must be less than 100 characters';
    }
    
    return errors;
  };
  
  // Validate password form
  const validatePasswordForm = () => {
    const errors = {};
    
    // Check if current password is entered
    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    
    // Check if new password is entered
    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    }
    
    // Check if passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    // Validate password complexity
    if (passwordData.newPassword) {
      const validation = validatePassword(passwordData.newPassword, currentUser.role);
      if (!validation.isValid) {
        errors.newPassword = validation.message;
      }
    }
    
    return errors;
  };
  
  // Handle profile form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset states
    setError(null);
    setSuccess(null);
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Prepare data for API
    const userData = {
      full_name: formData.fullName,
      email: formData.email,
      phone_number: formData.phoneNumber,
      language: formData.language,
      timezone: formData.timezone,
      softeon_username: formData.softeonUsername,
      lv_username: formData.lvUsername,
      locus_username: formData.locusUsername
    };
    
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_URL}/users/${currentUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Handle validation errors from server
        if (data.errors && Array.isArray(data.errors)) {
          const fieldErrors = {};
          data.errors.forEach(error => {
            fieldErrors[error.param] = error.msg;
          });
          setFormErrors(fieldErrors);
        }
        throw new Error(data.message || 'Failed to update profile');
      }
      
      // Update local storage with updated user data
      const updatedUser = { ...currentUser, ...data.user };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.message || 'An error occurred while updating your profile');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Reset states
    setPasswordErrors({});
    setPasswordSuccess(false);
    
    // Validate form
    const errors = validatePasswordForm();
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    try {
      setPasswordLoading(true);
      
      await changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      
      // Reset form and show success
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setPasswordSuccess(true);
      
      // Close modal after delay
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess(false);
      }, 2000);
      
    } catch (err) {
      setPasswordErrors({
        general: err.message || 'Failed to change password'
      });
    } finally {
      setPasswordLoading(false);
    }
  };
  
  // Render main profile form
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-slate-200">My Profile</h2>
        <button
          onClick={() => setShowPasswordModal(true)}
          className="flex items-center px-3 py-1.5 text-sm font-medium rounded bg-slate-700 hover:bg-slate-600 text-slate-200"
        >
          <Lock className="w-4 h-4 mr-2" />
          Change Password
        </button>
      </div>
      
      {/* Success message */}
      {success && (
        <div className="mb-4 p-3 bg-emerald-900/50 border border-emerald-800 rounded text-emerald-300 flex items-center">
          <Check className="w-5 h-5 mr-2" />
          {success}
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/50 border border-red-800 rounded text-red-300 flex items-center">
          <X className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Left column */}
          <div className="space-y-4">
            {/* Username (read-only) */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Username
              </label>
              <input
                type="text"
                value={currentUser?.username || ''}
                className="w-full p-2 rounded bg-slate-700/50 text-slate-300 border border-slate-600 focus:outline-none cursor-not-allowed"
                disabled
              />
              <p className="mt-1 text-xs text-slate-400">Username cannot be changed</p>
            </div>
            
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-slate-300 mb-1">
                Full Name
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                value={formData.fullName}
                onChange={handleChange}
                className="w-full p-2 rounded bg-slate-700 text-slate-200 border border-slate-600 focus:border-blue-500 focus:outline-none"
              />
            </div>
            
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full p-2 rounded bg-slate-700 text-slate-200 border focus:outline-none ${
                  formErrors.email ? 'border-red-500' : 'border-slate-600 focus:border-blue-500'
                }`}
              />
              {formErrors.email && (
                <p className="mt-1 text-xs text-red-400">{formErrors.email}</p>
              )}
            </div>
            
            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-slate-300 mb-1">
                Phone Number
              </label>
              <input
                id="phoneNumber"
                name="phoneNumber"
                type="text"
                value={formData.phoneNumber}
                onChange={handleChange}
                className={`w-full p-2 rounded bg-slate-700 text-slate-200 border focus:outline-none ${
                  formErrors.phoneNumber ? 'border-red-500' : 'border-slate-600 focus:border-blue-500'
                }`}
              />
              {formErrors.phoneNumber && (
                <p className="mt-1 text-xs text-red-400">{formErrors.phoneNumber}</p>
              )}
            </div>
          </div>
          
          {/* Right column */}
          <div className="space-y-4">
            {/* Role (read-only) */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Role
              </label>
              <input
                type="text"
                value={currentUser?.role || ''}
                className="w-full p-2 rounded bg-slate-700/50 text-slate-300 border border-slate-600 focus:outline-none cursor-not-allowed"
                disabled
              />
              <p className="mt-1 text-xs text-slate-400">Role cannot be changed</p>
            </div>
            
            {/* Language */}
            <div>
              <label htmlFor="language" className="block text-sm font-medium text-slate-300 mb-1">
                Language
              </label>
              <select
                id="language"
                name="language"
                value={formData.language}
                onChange={handleChange}
                className="w-full p-2 rounded bg-slate-700 text-slate-200 border border-slate-600 focus:border-blue-500 focus:outline-none"
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="hm">Hmong</option>
              </select>
            </div>
            
            {/* Timezone */}
            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-slate-300 mb-1">
                Timezone
              </label>
              <select
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                className="w-full p-2 rounded bg-slate-700 text-slate-200 border border-slate-600 focus:border-blue-500 focus:outline-none"
              >
                <option value="EST">Eastern Time (EST)</option>
                <option value="CST">Central Time (CST)</option>
                <option value="MST">Mountain Time (MST)</option>
                <option value="PST">Pacific Time (PST)</option>
                <option value="UTC">Universal Time (UTC)</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* External system credentials section */}
        {currentUser && (
          <div className="mt-8 mb-6">
            <h3 className="text-lg font-medium text-slate-200 mb-4">External System Credentials</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Softeon */}
              <div>
                <label htmlFor="softeonUsername" className="block text-sm font-medium text-slate-300 mb-1">
                  Softeon Username
                </label>
                <input
                  id="softeonUsername"
                  name="softeonUsername"
                  type="text"
                  value={formData.softeonUsername}
                  onChange={handleChange}
                  className={`w-full p-2 rounded bg-slate-700 text-slate-200 border focus:outline-none ${
                    formErrors.softeonUsername ? 'border-red-500' : 'border-slate-600 focus:border-blue-500'
                  }`}
                />
                {formErrors.softeonUsername && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.softeonUsername}</p>
                )}
              </div>
              
              {/* LV */}
              <div>
                <label htmlFor="lvUsername" className="block text-sm font-medium text-slate-300 mb-1">
                  Logistiview Username
                </label>
                <input
                  id="lvUsername"
                  name="lvUsername"
                  type="text"
                  value={formData.lvUsername}
                  onChange={handleChange}
                  className={`w-full p-2 rounded bg-slate-700 text-slate-200 border focus:outline-none ${
                    formErrors.lvUsername ? 'border-red-500' : 'border-slate-600 focus:border-blue-500'
                  }`}
                />
                {formErrors.lvUsername && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.lvUsername}</p>
                )}
              </div>
              
              {/* Locus */}
              <div>
                <label htmlFor="locusUsername" className="block text-sm font-medium text-slate-300 mb-1">
                  Locus Username
                </label>
                <input
                  id="locusUsername"
                  name="locusUsername"
                  type="text"
                  value={formData.locusUsername}
                  onChange={handleChange}
                  className={`w-full p-2 rounded bg-slate-700 text-slate-200 border focus:outline-none ${
                    formErrors.locusUsername ? 'border-red-500' : 'border-slate-600 focus:border-blue-500'
                  }`}
                />
                {formErrors.locusUsername && (
                  <p className="mt-1 text-xs text-red-400">{formErrors.locusUsername}</p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Submit button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
      
      {/* Password change modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg shadow-lg max-w-md w-full p-6 border border-slate-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-slate-200">Change Password</h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Password change success message */}
            {passwordSuccess && (
              <div className="mb-4 p-3 bg-emerald-900/50 border border-emerald-800 rounded text-emerald-300 flex items-center">
                <Check className="w-5 h-5 mr-2" />
                Password changed successfully!
              </div>
            )}
            
            {/* Password change error message */}
            {passwordErrors.general && (
              <div className="mb-4 p-3 bg-red-900/50 border border-red-800 rounded text-red-300 flex items-center">
                <X className="w-5 h-5 mr-2" />
                {passwordErrors.general}
              </div>
            )}
            
            <form onSubmit={handlePasswordSubmit}>
              {/* Current Password */}
              <div className="mb-4">
                <label htmlFor="currentPassword" className="block text-sm font-medium text-slate-300 mb-1">
                  Current Password
                </label>
                <input
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className={`w-full p-2 rounded bg-slate-700 text-slate-200 border focus:outline-none ${
                    passwordErrors.currentPassword ? 'border-red-500' : 'border-slate-600 focus:border-blue-500'
                  }`}
                  required
                />
                {passwordErrors.currentPassword && (
                  <p className="mt-1 text-xs text-red-400">{passwordErrors.currentPassword}</p>
                )}
              </div>
              
              {/* New Password */}
              <div className="mb-4">
                <label htmlFor="newPassword" className="block text-sm font-medium text-slate-300 mb-1">
                  New Password
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className={`w-full p-2 rounded bg-slate-700 text-slate-200 border focus:outline-none ${
                    passwordErrors.newPassword ? 'border-red-500' : 'border-slate-600 focus:border-blue-500'
                  }`}
                  required
                />
                {passwordErrors.newPassword && (
                  <p className="mt-1 text-xs text-red-400">{passwordErrors.newPassword}</p>
                )}
                
                {/* Password requirements */}
                {passwordData.newPassword && passwordValidation && (
                  <PasswordRequirements 
                    role={currentUser.role} 
                    validationState={passwordValidation} 
                  />
                )}
              </div>
              
              {/* Confirm Password */}
              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-1">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className={`w-full p-2 rounded bg-slate-700 text-slate-200 border focus:outline-none ${
                    passwordErrors.confirmPassword ? 'border-red-500' : 'border-slate-600 focus:border-blue-500'
                  }`}
                  required
                />
                {passwordErrors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-400">{passwordErrors.confirmPassword}</p>
                )}
              </div>
              
              {/* Submit buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium rounded focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors disabled:opacity-50"
                >
                  {passwordLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    "Change Password"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default Profile;